import i18n from "@/i18n";
import UtilService from "@/services/UtilService";

export interface TableAvailability {
  text: string;
  value: string;
  hint: string;
  colour: string;
}

export interface TablePreference {
  text: string;
  value: string;
  colour: string;
}

export default class Table {
  tableId!: number;
  tableName!: string;
  minCapacity!: number;
  maxCapacity!: number;
  roomId!: number;
  availability!: string;
  preference!: string;
  joinedTableComponents: Table[] = [];
  joinedTableComponentOf: number[] = [];

  constructor(jsonObj?: Table) {
    if (jsonObj) {
      Table.updateTable(this, jsonObj);
    }
  }

  static updateTable(toUpdate: Table, changed: Table) {
    toUpdate.tableId = changed.tableId;
    toUpdate.tableName = changed.tableName;
    toUpdate.minCapacity = changed.minCapacity;
    toUpdate.maxCapacity = changed.maxCapacity;
    toUpdate.roomId = changed.roomId;
    toUpdate.availability = changed.availability;
    toUpdate.preference = changed.preference;
    if (changed.joinedTableComponents !== null)
      toUpdate.joinedTableComponents = changed.joinedTableComponents;
    if (changed.joinedTableComponentOf !== null)
      toUpdate.joinedTableComponentOf = changed.joinedTableComponentOf;

    toUpdate.joinedTableComponents.sort(this.TablesByNameAscendingComparator);
  }

  static copy(toCopy: Table): Table {
    return new Table(JSON.parse(JSON.stringify(toCopy)));
  }

  static getAllAffectedTableIds(table: Table) {
    const toReturn = [];
    if (table == null) return [];
    toReturn.push(table.tableId);
    for (let j = 0; j < table.joinedTableComponents.length; j++) {
      toReturn.push(table.joinedTableComponents[j].tableId);
    }
    for (let j = 0; j < table.joinedTableComponentOf.length; j++) {
      toReturn.push(table.joinedTableComponentOf[j]);
    }
    return toReturn;
  }

  static haveDifferences(table1: Table, table2: Table): boolean {
    return !UtilService.equals(table1, table2);
  }

  static tableAvailability: TableAvailability[] = [
    {
      text: i18n.tc("table.availability.available.message"),
      value: "AVAILABLE",
      hint: i18n.tc("table.availability.available.hint"),
      colour: "green"
    },
    {
      text: i18n.tc("table.availability.reserved.message"),
      value: "RESERVED",
      hint: i18n.tc("table.availability.reserved.hint"),
      colour: "orange"
    },
    {
      text: i18n.tc("table.availability.unavailable.message"),
      value: "UNAVAILABLE",
      hint: i18n.tc("table.availability.unavailable.hint"),
      colour: "red"
    }
  ];

  static tablePreference: TablePreference[] = [
    {
      text: i18n.tc("table.preference.high.message"),
      value: "HIGH",
      colour: "green"
    },
    {
      text: i18n.tc("table.preference.medium.message"),
      value: "MEDIUM",
      colour: "orange"
    },
    {
      text: i18n.tc("table.preference.low.message"),
      value: "LOW",
      colour: "red"
    }
  ];

  static getTableAvailabilityColour(table: Table): string {
    const availability = Table.getTableAvailability(table);
    if (availability != null) return availability.colour;
    return "";
  }

  static getTablePreferenceColour(table: Table): string {
    const preference = Table.getTablePreference(table);
    if (preference != null) return preference.colour;
    return "";
  }

  static getTablePreference(table: Table): TablePreference | null {
    for (let i = 0; i < Table.tablePreference.length; i++)
      if (table.preference === Table.tablePreference[i].value)
        return Table.tablePreference[i];
    return null;
  }

  static getTableAvailability(table: Table): TableAvailability | null {
    for (let i = 0; i < Table.tableAvailability.length; i++)
      if (table.availability === Table.tableAvailability[i].value)
        return Table.tableAvailability[i];
    return null;
  }

  static tableAvailabilityDisplayNames(): { [value: string]: string } {
    const toReturn: { [value: string]: string } = {};
    for (let i = 0; i < Table.tableAvailability.length; i++) {
      toReturn[Table.tableAvailability[i].value] =
        Table.tableAvailability[i].text;
    }
    return toReturn;
  }

  static tablePreferenceDisplayNames(): { [value: string]: string } {
    const toReturn: { [value: string]: string } = {};
    for (let i = 0; i < Table.tablePreference.length; i++) {
      toReturn[Table.tablePreference[i].value] = Table.tablePreference[i].text;
    }
    return toReturn;
  }

  static tableAvailabilityHints(): { [value: string]: string } {
    const toReturn: { [value: string]: string } = {};
    for (let i = 0; i < Table.tableAvailability.length; i++) {
      toReturn[Table.tableAvailability[i].value] =
        Table.tableAvailability[i].hint;
    }
    return toReturn;
  }

  static getComponentMockToSend(newJoinedTable: Table) {
    const mockArray: Table[] = [];
    newJoinedTable.joinedTableComponents.forEach(component =>
      mockArray.push(Table.getMockTable(component))
    );
    newJoinedTable.joinedTableComponents = mockArray;
  }

  private static getMockTable(component: Table) {
    const toReturn = Table.copy(component);
    delete toReturn.tableName;
    delete toReturn.maxCapacity;
    delete toReturn.minCapacity;
    delete toReturn.joinedTableComponents;
    delete toReturn.joinedTableComponentOf;
    delete toReturn.roomId;
    delete toReturn.availability;
    return toReturn;
  }

  public static suitableFor(table: Table, numberOfPeople: number): boolean {
    return (
      numberOfPeople > table.maxCapacity || numberOfPeople < table.minCapacity
    );
  }

  public static getCopyWithChangedValues(
    original: Table,
    changed: Table
  ): Table {
    const toReturn = Table.copy(changed);
    if (original.tableName === changed.tableName) delete toReturn.tableName;
    if (original.minCapacity === changed.minCapacity)
      delete toReturn.minCapacity;
    if (original.maxCapacity === changed.maxCapacity)
      delete toReturn.maxCapacity;

    if (original.availability === changed.availability)
      delete toReturn.availability;
    delete toReturn.joinedTableComponentOf;
    delete toReturn.roomId;

    return toReturn;
  }

  static TablesByNameAscendingComparator = (table1: Table, table2: Table) => {
    const name1 = table1.tableName.toString().toUpperCase();
    const name2 = table2.tableName.toString().toUpperCase();
    return UtilService.stringComparatorAscending(name1, name2);
  };
}
